// BUILD-042 (Oct 24, cycle 12) - HR management overview of ONE employee: the admin-side destination after initial setup (distinct from the employee's own My HR).
//
// Everything on this screen derives from REAL saved objects for THIS employee: the admin attention data the page already loads (documents awaiting HR review,
// open checklist tasks, documents waiting on the employee), the employee's saved terms, and the server's setup status. No sample rows, no pay or other
// restricted data. A check that is still running, was refused or failed says exactly that (with Retry where it helps) and is NEVER drawn as "nothing needs
// attention"; a real empty state is shown only when the read succeeded and found none.
// Action buttons appear only when the caller's capabilities allow (unknown = shown; the server re-checks every action) and each opens an EXISTING destination.
// "Edit" changes details through the existing employment-change and personal-detail routes and stays on this screen; it never restarts initial setup.
import { useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { HrAdminEmployeeDetailResponse } from '../../../api/client';
import { refreshAttention } from '../attentionStore';
import { useHrAccess } from '../HrAccessContext';
import { formatLocalDate } from '../hrFormat';
import { adminItemPath, adminStateLabel, employeeSectionCounts, type AdminAttention, type AdminAttentionItem, type AttentionSlice } from '../workspace/attentionModel';
import type { SectionKey } from '../workspace/sectionGroups';
import {
  EmployeeDetailsFields, OWN_RECORD_MESSAGE, detailsChanged, saveEmployeeDetails, useDirectoryOptions, valuesFromBaseline,
  type DetailsBaseline, type DetailsValues,
} from './employeeDetails';
import { OUTLINE_BTN, PRIMARY_BTN } from './stepUi';
import { defaultEffectiveDate, displayTerm, setupPath, todayPhoenix } from './setupModel';
import type { SetupGate } from './useSetupGate';

type Term = HrAdminEmployeeDetailResponse['termHistory'][number];

/** How many rows each attention card shows before "Show all" (the rest stay one click away, never hidden). */
const CARD_ROWS = 5;

function titleCase(s: string) { return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : s; }

function Icon({ d }: { d: string }) {
  return <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0"><path d={d} /></svg>;
}
const ICONS = {
  edit: 'M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4',
  checklist: 'M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2',
  document: 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6',
  equipment: 'M4 6h16v10H4zM2 19h20',
  clock: 'M12 7v5l3 2M12 21a9 9 0 100-18 9 9 0 000 18z',
};

/** The red-circle mark of "Needs HR attention" (colour AND a "!" glyph; the heading carries the words). */
function AlertCircle() {
  return (
    <span className="w-5 h-5 rounded-full bg-red-100 text-red-700 border border-red-300 flex items-center justify-center text-[11px] font-bold flex-shrink-0" aria-hidden="true" data-testid="icon-needs-hr">!</span>
  );
}
/** The blue clock of "Waiting on employee". */
function WaitClock() {
  return <span className="w-5 h-5 rounded-full bg-[#E3F1FB] text-[#176A9B] flex items-center justify-center flex-shrink-0" aria-hidden="true" data-testid="icon-waiting"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={ICONS.clock} /></svg></span>;
}

function RowTile({ kind }: { kind: AdminAttentionItem['kind'] }) {
  const d = kind === 'task' ? ICONS.checklist : ICONS.document;
  return <span className="w-8 h-8 rounded-md bg-[#EDF5FC] text-[#176A9B] flex items-center justify-center flex-shrink-0"><Icon d={d} /></span>;
}

const isEmployeeRole = (role: string | null) => (role ?? '').trim().toLowerCase() === 'employee';

type AttentionView = 'loading' | 'ready' | 'denied' | 'unavailable';

export function AdminEmployeeOverview({
  employeeId, viewerEmployeeId, record, names, departmentName, attention, gate, onNavigate, onChanged,
}: {
  employeeId: string;
  viewerEmployeeId: string;
  record: HrAdminEmployeeDetailResponse;
  names: Record<string, string | null>;
  departmentName: (id: string | null | undefined) => string | null;
  attention: AttentionSlice<AdminAttention>;
  gate: SetupGate;
  onNavigate: (key: SectionKey) => void;
  /** Re-read the employee record after an edit. */
  onChanged: () => void;
}) {
  const { state: access } = useHrAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const surfaces = access.kind === 'ready' ? access.me.surfaces : null;
  // undefined (unknown / older server) is shown; false hides. The server re-checks every action either way.
  const mayEdit = surfaces ? surfaces.hrAdmin !== false : true;
  const mayChecklists = surfaces?.mayManageEmployeeChecklists !== false;
  const mayDocuments = surfaces?.mayManageDocuments !== false;
  const mayFollowUp = surfaces ? surfaces.hrAdmin !== false : true;
  const isOwnRecord = employeeId === viewerEmployeeId;

  const name = record.name ?? 'This employee';
  const status = record.period?.employmentStatus ?? null;
  const progress = gate.view?.progress ?? null;
  const finishedNow = !!(location.state as { setupFinished?: boolean } | null)?.setupFinished;
  const [noteDismissed, setNoteDismissed] = useState(false);
  function dismissNote() {
    setNoteDismissed(true);
    // The router state that said "just finished" is spent: a reload no longer repeats the message.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }

  // ── Employee details: the server's own reading of "what applies now" (also carries phone), else the saved terms ─────────────────────
  const today = todayPhoenix();
  const fallbackTerm = displayTerm(record.termHistory, today);
  const baseline: DetailsBaseline = gate.view?.summary.details ?? {
    hireDate: record.period?.hireDate ?? null, title: fallbackTerm?.title ?? null, departmentId: fallbackTerm?.departmentId ?? null,
    managerEmployeeId: fallbackTerm?.managerEmployeeId ?? null, classification: fallbackTerm?.classification ?? null, phone: null, phoneAvailable: false,
  };

  const [editing, setEditing] = useState(false);
  const detailsCardRef = useRef<HTMLElement | null>(null);
  const options = useDirectoryOptions();
  const [values, setValues] = useState<DetailsValues>(() => valuesFromBaseline(baseline));
  const [effectiveDate, setEffectiveDate] = useState(() => defaultEffectiveDate(baseline.hireDate, today));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [editMsg, setEditMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  function startEdit() {
    if (!editing) { setValues(valuesFromBaseline(baseline)); setEffectiveDate(defaultEffectiveDate(baseline.hireDate, todayPhoenix())); setReason(''); setEditMsg(null); setEditing(true); }
    // Both "Edit details" (top) and the card's "Edit" open the SAME single editor; bring it into view.
    requestAnimationFrame(() => detailsCardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }
  async function saveEdit() {
    if (saving) return;
    setSaving(true); setEditMsg(null);
    const r = await saveEmployeeDetails({ employeeId, effectiveDate, baseline, values, reason });
    if (!r.ok) {
      setEditMsg({ kind: 'error', text: `Not saved. ${r.message}${r.saved ? ` Saved: ${r.saved}.` : ''}${r.notSaved ? ` Not saved: ${r.notSaved}.` : ''}` });
      if (r.saved) { onChanged(); await gate.reload(); }
      setSaving(false);
      return;
    }
    if (r.wrote) { onChanged(); await gate.reload(); }
    setEditMsg({ kind: 'ok', text: r.wrote ? 'Employee details saved.' : 'Nothing changed.' });
    setEditing(false);
    setSaving(false);
  }
  const pending = editing && (() => { const c = detailsChanged(baseline, values); return c.job.length > 0 || c.phone; })();

  // Assign checklist always lands on the ONBOARDING view of the checklists section (a stale ?tab=offboarding must not decide it).
  function openOnboarding() {
    const next = new URLSearchParams(params);
    next.set('tab', 'onboarding');
    setParams(next, { replace: true });
    onNavigate('checklists');
  }

  // ── Needs HR attention / Waiting on employee: this employee's real items only ──────────────────────────────────────────────────────
  const counts = employeeSectionCounts(attention, employeeId);
  const mine: AdminAttentionItem[] = attention.status === 'error' || !attention.data ? [] : attention.data.items.filter((i) => i.employeeId === employeeId);
  const waiting = mine.filter((i) => i.kind === 'awaiting_employee' || (i.kind === 'task' && isEmployeeRole(i.responsibleRole)));
  const needsHr = mine.filter((i) => !waiting.includes(i));
  // What this screen may say about the check. A failed or refused read is its own state and is never "nothing needs attention".
  const view: AttentionView = attention.status === 'error' ? 'unavailable'
    : attention.data?.authorized === false ? 'denied'
    : attention.data ? 'ready'
    : !mayFollowUp ? 'denied'
    : 'loading';
  const [showAll, setShowAll] = useState<{ needs: boolean; waiting: boolean }>({ needs: false, waiting: false });
  // The server's exact total can exceed the rows it sent (a capped list); say so rather than imply the cards are complete.
  const unlisted = view === 'ready' ? Math.max(0, counts.total - mine.length) : 0;

  // ── Recent HR activity: the saved employment changes (the history this page already loads) ─────────────────────────────────────────
  const recent = useMemo(() => [...record.termHistory].sort((a, b) => (a.effectiveDate < b.effectiveDate ? 1 : a.effectiveDate > b.effectiveDate ? -1 : 0)).slice(0, 6), [record.termHistory]);
  const describe = (t: Term): string => {
    const parts: string[] = [];
    if (t.title) parts.push(t.title);
    const dn = departmentName(t.departmentId); if (t.departmentId && dn) parts.push(dn);
    if (t.managerEmployeeId) parts.push(`manager ${names[t.managerEmployeeId] ?? 'unavailable'}`);
    return parts.join(' · ');
  };

  const managerName = baseline.managerEmployeeId ? (names[baseline.managerEmployeeId] ?? record.managerName ?? null) : null;
  const deptName = baseline.departmentId ? departmentName(baseline.departmentId) : null;

  const statusBadge = status
    ? status.toLowerCase() === 'active'
      ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-green-100 text-green-800" data-testid="badge-employment"><span className="w-1.5 h-1.5 rounded-full bg-green-500" aria-hidden="true" />Active employee</span>
      : <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700" data-testid="badge-employment">{titleCase(status)}</span>
    : null;
  // Finished = the blue badge. Legacy (no setup row) = nothing. Unfinished = amber words with a way back to setup.
  const setupBadge = progress?.status === 'finished'
    ? <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#E3F1FB] text-[#176A9B]" data-testid="badge-setup-finished">HR setup finished</span>
    : progress?.status === 'in_progress'
      ? <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-900" data-testid="badge-setup-progress">HR setup in progress</span>
      : null;

  const stateWords = (i: AdminAttentionItem): string => {
    if (i.kind === 'task') return `${i.state === 'in_progress' ? 'In progress' : 'Not started'}${i.responsibleRole ? ` · Responsible: ${i.responsibleRole}` : ''}`;
    if (i.kind === 'review') return 'Signed copy waiting for HR review';
    return adminStateLabel(i);
  };
  const itemRow = (i: AdminAttentionItem, kind: 'needs' | 'waiting') => {
    const word = kind === 'needs' && (i.kind === 'review' || i.kind === 'correction') ? 'Review' : 'Open';
    return (
      <li key={i.itemId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5" data-testid="overview-attention-item" data-kind={i.kind} data-state={i.state}>
        <RowTile kind={i.kind} />
        <div className="min-w-0 flex-1 basis-32">
          <div className="text-sm font-semibold text-gray-900 break-words">{i.title}</div>
          <div className="text-xs text-gray-500 break-words">
            {i.kind === 'task' && i.detail ? `${i.detail} · ` : ''}{stateWords(i)}{i.dueDate ? ` · due ${formatLocalDate(i.dueDate)}` : ''}
          </div>
        </div>
        <button type="button" onClick={() => navigate(adminItemPath(i))} className="text-sm font-medium text-[#176A9B] hover:underline flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] rounded" aria-label={`${word}: ${i.title}`} data-testid="overview-item-link">
          {word} →
        </button>
      </li>
    );
  };
  const listState = (items: AdminAttentionItem[], emptyText: string, kind: 'needs' | 'waiting') => {
    if (view === 'unavailable') {
      return (
        <div role="alert" className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex flex-wrap items-center justify-between gap-2" data-testid="overview-attention-error">
          <span className="min-w-0">We couldn’t check what’s open for this employee just now. This is not the same as nothing being open.</span>
          <button type="button" onClick={refreshAttention} className="text-sm font-medium text-[#176A9B] border border-[#4AABDE] rounded-md px-3 py-1 hover:bg-[#EDF5FC] flex-shrink-0" data-testid="overview-attention-retry">Retry</button>
        </div>
      );
    }
    if (view === 'denied') {
      return <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2" data-testid="overview-attention-denied">This account doesn’t have access to HR follow-up items, so they can’t be shown here. That doesn’t mean nothing is open.</p>;
    }
    if (view === 'loading') {
      return (
        <div role="status" aria-live="polite" data-testid="overview-attention-loading">
          <p className="text-sm text-gray-500">Checking what’s open…</p>
          <div aria-hidden="true" className="mt-2 space-y-2 animate-pulse"><div className="h-8 rounded bg-gray-100" /><div className="h-8 rounded bg-gray-100 w-5/6" /></div>
        </div>
      );
    }
    if (items.length === 0) {
      // Zero rows is a real "nothing" only when the server's exact count for this card agrees; a capped list can hide rows.
      const hiddenHere = unlisted > 0;
      return hiddenHere
        ? <p className="text-sm text-gray-600" data-testid={`overview-${kind}-unlisted`}>The list of open items for this employee is longer than what’s loaded here. Use “View all pending work” below.</p>
        : <p className="text-sm text-gray-500" data-testid={`overview-${kind}-none`}>{emptyText}</p>;
    }
    const expanded = showAll[kind];
    const rows = expanded ? items : items.slice(0, CARD_ROWS);
    return (
      <>
        <ul className="divide-y divide-gray-100" data-testid={`overview-${kind}-list`}>{rows.map((i) => itemRow(i, kind))}</ul>
        {items.length > CARD_ROWS && (
          <button type="button" className="mt-1 text-xs text-[#176A9B] hover:underline" onClick={() => setShowAll((s) => ({ ...s, [kind]: !expanded }))} aria-expanded={expanded} data-testid={`overview-${kind}-toggle`}>
            {expanded ? 'Show fewer' : `Show all ${items.length}`}
          </button>
        )}
      </>
    );
  };

  // Optional blanks (job title, department, manager) are neutral text, never a warning. Hire date has no "Not assigned" (it always exists on a record).
  const detailRow = (label: string, value: string | null, optional: boolean) => (
    <div className="min-w-0" data-testid={`detail-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm font-semibold text-gray-900 mt-0.5 break-words">
        {value ? value : optional ? <span className="text-gray-500 font-normal" data-testid="not-assigned">Not assigned</span> : '—'}
      </dd>
    </div>
  );

  const countPill = (n: number, tone: 'red' | 'blue') => (
    <span className={`ml-1 text-xs font-semibold rounded-full px-2 py-0.5 ${tone === 'red' ? 'text-red-800 bg-red-50 border border-red-200' : 'text-[#176A9B] bg-[#EDF5FC] border border-[#BBD9EE]'}`}>{n}</span>
  );

  return (
    <div className="space-y-4" data-testid="admin-employee-overview">
      {finishedNow && !noteDismissed && (
        <div role="status" className="rounded-md border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-900 flex items-center justify-between gap-3" data-testid="setup-finished-note">
          <span>Initial HR setup finished. Track outstanding tasks and documents below.</span>
          <button type="button" className="text-xs underline flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 rounded" onClick={dismissNote} aria-label="Dismiss message" data-testid="setup-finished-dismiss">Dismiss</button>
        </div>
      )}

      <header>
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 break-words" data-testid="overview-name">{name} — HR management</h1>
        <p className="text-sm text-gray-600 mt-0.5" data-testid="overview-subtitle">Manage this employee's records and follow-up.</p>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 mt-2.5" data-testid="overview-status-row">
          {statusBadge}{setupBadge}
          {record.period?.hireDate && <span className="text-xs text-gray-600" data-testid="overview-hired">Hired {formatLocalDate(record.period.hireDate)}</span>}
          {progress?.status === 'in_progress' && <Link to={setupPath(employeeId)} className="text-xs font-medium text-[#176A9B] hover:underline" data-testid="overview-continue-setup">Continue setup →</Link>}
        </div>
      </header>

      <div className="flex flex-wrap gap-2" role="group" aria-label="HR actions" data-testid="overview-actions">
        {mayEdit && <button type="button" className={OUTLINE_BTN} onClick={startEdit} data-testid="action-edit-details"><Icon d={ICONS.edit} />Edit details</button>}
        {mayChecklists && <button type="button" className={OUTLINE_BTN} onClick={openOnboarding} data-testid="action-assign-checklist"><Icon d={ICONS.checklist} />Assign checklist</button>}
        {mayDocuments && <button type="button" className={OUTLINE_BTN} onClick={() => onNavigate('documents')} data-testid="action-assign-documents"><Icon d={ICONS.document} />Assign documents</button>}
        {mayDocuments && <button type="button" className={OUTLINE_BTN} onClick={() => onNavigate('property')} data-testid="action-issue-equipment"><Icon d={ICONS.equipment} />Issue equipment</button>}
      </div>

      {gate.state === 'error' && (
        <p role="alert" className="text-xs text-amber-800" data-testid="overview-setup-unknown">Initial HR setup status could not be checked ({gate.error}). Details below come from the saved employment record.</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="min-w-0 bg-white rounded-lg border border-gray-200 shadow-sm p-5" aria-labelledby="ov-needs" data-testid="overview-needs-hr">
          <h2 id="ov-needs" className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2"><AlertCircle />Needs HR attention{view === 'ready' && needsHr.length > 0 ? countPill(needsHr.length, 'red') : null}</h2>
          {listState(needsHr, 'Nothing needs HR attention for this employee.', 'needs')}
        </section>
        <section className="min-w-0 bg-white rounded-lg border border-gray-200 shadow-sm p-5" aria-labelledby="ov-waiting" data-testid="overview-waiting">
          <h2 id="ov-waiting" className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2"><WaitClock />Waiting on employee{view === 'ready' && waiting.length > 0 ? countPill(waiting.length, 'blue') : null}</h2>
          {listState(waiting, 'Nothing is waiting on this employee.', 'waiting')}
        </section>
      </div>
      {view === 'ready' && counts.total > 0 && unlisted > 0 && (
        <button type="button" onClick={() => navigate('/hr/admin?tab=open')} className="text-xs text-[#176A9B] hover:underline" data-testid="overview-more-pending">View all pending work →</button>
      )}

      <section ref={detailsCardRef} className="bg-white rounded-lg border border-gray-200 shadow-sm p-5" aria-labelledby="ov-details" data-testid="overview-details-card">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 id="ov-details" className="text-sm font-bold text-gray-900">Employee details</h2>
          {mayEdit && !editing && <button type="button" className={`${OUTLINE_BTN} !py-1 !px-3`} onClick={startEdit} data-testid="details-edit">Edit</button>}
        </div>
        {editMsg && <p role={editMsg.kind === 'error' ? 'alert' : 'status'} className={`text-sm mb-3 ${editMsg.kind === 'error' ? 'text-red-700' : 'text-green-800'}`} data-testid="details-edit-message">{editMsg.text}</p>}
        {!editing ? (
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
            {detailRow('Hire date', baseline.hireDate ? formatLocalDate(baseline.hireDate) : null, false)}
            {detailRow('Job title', baseline.title, true)}
            {detailRow('Department', deptName ?? (baseline.departmentId ? 'Assigned (name unavailable)' : null), true)}
            {detailRow('Manager', managerName ?? (baseline.managerEmployeeId ? 'Assigned (name unavailable)' : null), true)}
            {/* A stored classification is shown for reference only; an empty one is simply absent (never a warning, never a prompt). */}
            {baseline.classification ? detailRow('Classification', baseline.classification, false) : null}
          </dl>
        ) : (
          <div data-testid="details-editor">
            {isOwnRecord && <p className="mb-3 rounded-md bg-[#EDF5FC] px-3 py-2 text-xs text-gray-700" data-testid="own-record-change-note">{OWN_RECORD_MESSAGE}</p>}
            <EmployeeDetailsFields
              idPrefix="overview-edit"
              employeeId={employeeId}
              person={{ name, email: null }}
              baseline={baseline}
              values={values}
              onChange={setValues}
              options={options}
              disabled={isOwnRecord || saving}
              hireDateNote="The hire date is set when HR setup starts and isn’t edited here."
              extra={(
                <>
                  <div className="flex flex-col gap-1">
                    <label htmlFor="overview-edit-effective" className="text-xs font-medium text-gray-700">Effective date</label>
                    <input id="overview-edit-effective" type="date" className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full bg-white" value={effectiveDate} disabled={isOwnRecord || saving} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="overview-edit-effective" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label htmlFor="overview-edit-reason" className="text-xs font-medium text-gray-700">Reason (optional)</label>
                    <input id="overview-edit-reason" className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full bg-white" value={reason} disabled={isOwnRecord || saving} onChange={(e) => setReason(e.target.value)} placeholder="Why this change" />
                  </div>
                </>
              )}
            />
            {options.error && <p role="alert" className="mt-3 text-xs text-red-700">{options.error} <button type="button" className="underline" onClick={options.retry}>Try again</button></p>}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button type="button" className={PRIMARY_BTN} onClick={saveEdit} disabled={saving || isOwnRecord} data-testid="details-save">{saving ? 'Saving…' : 'Save changes'}</button>
              <button type="button" className={OUTLINE_BTN} onClick={() => { setEditing(false); setEditMsg(null); }} disabled={saving} data-testid="details-cancel">Cancel</button>
              {pending && <span className="text-xs text-amber-800" data-testid="details-unsaved">● Unsaved changes</span>}
            </div>
            <p className="text-xs text-gray-500 mt-2">Changes are recorded as an employment change and appear in the activity log. This does not restart initial HR setup.</p>
          </div>
        )}
      </section>

      <details className="bg-white rounded-lg border border-gray-200 shadow-sm" data-testid="overview-recent-activity">
        <summary className="px-5 py-3 text-sm font-bold text-gray-900 cursor-pointer flex items-center gap-2"><Icon d={ICONS.clock} />Recent HR activity</summary>
        <div className="px-5 pb-4">
          {recent.length === 0 ? <p className="text-sm text-gray-500">No employment changes are recorded yet.</p> : (
            <ul className="divide-y divide-gray-100">
              {recent.map((t, i) => (
                <li key={`${t.effectiveDate}-${i}`} className="py-2 text-sm text-gray-800" data-testid="recent-activity-item">
                  <span className="font-medium">{formatLocalDate(t.effectiveDate)}</span>
                  {t.reason ? <span className="text-gray-500"> · {t.reason.replace(/-/g, ' ')}</span> : null}
                  {describe(t) ? <span className="block text-xs text-gray-600">{describe(t)}</span> : null}
                </li>
              ))}
            </ul>
          )}
          <button type="button" onClick={() => onNavigate('history')} className="mt-2 text-xs text-[#176A9B] hover:underline">View history &amp; corrections →</button>
        </div>
      </details>
    </div>
  );
}
